import { RankingInfo } from '@tanstack/match-sorter-utils'
import {
    FilterFn,
    PaginationState,
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable
} from '@tanstack/react-table'
import React, { useEffect, useState } from 'react'

import { LinkIcon } from '@heroicons/react/24/outline'
import { CopyToClipboardWrapper } from '../../components/CopyToClipboard'
import Spinner from '../../components/Spinner'
import AdminActionBar from '../../components/admin/AdminActionBar'
import PageMasthead from '../../components/PageMasthead'
import SimplePagination from '../../components/table/SimplePagination'
import SimpleTable from '../../components/table/SimpleTable'
import { fuzzyFilter } from '../../components/table/helpers'
import { ReferrerView, useListReferrersQuery } from '../../api/client'

declare module '@tanstack/table-core' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>
    }
    interface FilterMeta {
        itemRank: RankingInfo
    }
}

const columnHelper = createColumnHelper<ReferrerView>()

function FeatureBadge({ enabled }: { enabled: boolean }) {
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                enabled
                    ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
                    : 'bg-ink-100 text-ink-500'
            }`}
        >
            {enabled ? 'Yes' : 'No'}
        </span>
    )
}

function ReferrerListPage() {
    const [data, setData] = useState<ReferrerView[]>([])
    const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10
    })

    const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize])

    const baseUrl = `${window.location.protocol}//${window.location.host}`

    const { data: getReferrersData, loading: getReferrersLoading } = useListReferrersQuery({
        variables: {
            offset: pageIndex * pageSize,
            limit: pageSize
        }
    })

    const referrerCount = getReferrersData?.listReferrers?.total || 0

    const columns = [
        columnHelper.accessor('referrer', {
            header: () => <div className="text-left ml-6">Referrer Name</div>,
            cell: (info) => (
                <div className="flex items-center ml-2">
                    <div className="ml-3">
                        <div className="text-sm font-medium text-ink-800">{info.getValue()}</div>
                        <div className="text-sm text-ink-500">ID: {info.row.original.id}</div>
                    </div>
                </div>
            )
        }),
        columnHelper.display({
            id: 'sharableUrl',
            header: () => 'Sharable URL',
            cell: (info) => {
                const url = `${baseUrl}/#/?referrer=${info.row.original.referrerUuid}`
                return (
                    <CopyToClipboardWrapper text={url}>
                        <span className="text-sm text-ink-600 truncate max-w-xs block">{url}</span>
                    </CopyToClipboardWrapper>
                )
            }
        }),
        columnHelper.accessor('userCount', {
            header: () => 'Users',
            cell: (info) => <span className="text-sm">{info.getValue()}</span>
        }),
        columnHelper.accessor('userLimit', {
            header: () => 'User Limit',
            cell: (info) => <span className="text-sm">{info.getValue() ?? 'Unlimited'}</span>
        }),
        // columnHelper.accessor('signUpEnabled', {
        //     header: () => 'Sign Up',
        //     cell: (info) => <FeatureBadge enabled={info.getValue()} />
        // }),
        // columnHelper.accessor('requireSubscription', {
        //     header: () => 'Subscription',
        //     cell: (info) => <FeatureBadge enabled={info.getValue()} />
        // }),
        // columnHelper.accessor('allowTrial', {
        //     header: () => 'Trial',
        //     cell: (info) => <FeatureBadge enabled={info.getValue()} />
        // }),
        // columnHelper.accessor('trialDays', {
        //     header: () => 'Trial Days',
        //     cell: (info) => <span className="text-sm">{info.getValue() ?? '-'}</span>
        // }),
        // columnHelper.accessor('creditCardRequired', {
        //     header: () => 'CC Required',
        //     cell: (info) => <FeatureBadge enabled={info.getValue()} />
        // }),
        // columnHelper.accessor('isCobranded', {
        //     header: () => 'Cobranded',
        //     cell: (info) => <FeatureBadge enabled={info.getValue()} />
        // }),
        columnHelper.accessor('isGuidedOnboarding', {
            header: () => 'Guided',
            cell: (info) => <FeatureBadge enabled={info.getValue()} />
        })
    ]

    useEffect(() => {
        if (getReferrersData?.listReferrers?.referrers) {
            setData(getReferrersData.listReferrers.referrers)
        }
    }, [getReferrersData])

    const table = useReactTable({
        data,
        columns,
        filterFns: {
            fuzzy: fuzzyFilter
        },
        pageCount: Math.ceil(referrerCount / pageSize),
        state: { pagination },
        onPaginationChange: setPagination,
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    })

    return (
        // Grey page backdrop behind the white list card, shared across admin lists.
        <div className="flex min-h-0 w-full flex-1 flex-col bg-gray-50">
            <PageMasthead
                title="Referrer management"
                tabTitle="Referrer management"
                subtitle={`${data.length} referrer${data.length !== 1 ? 's' : ''} on this page${
                    referrerCount > data.length ? ` of ${referrerCount} total` : ''
                }`}
            />

            {/* Total referrers, previously shown as a quick stat in the header. */}
            <div className="flex items-center gap-1.5 px-6 pt-4 font-sans text-sm text-ink-500">
                <LinkIcon className="h-4 w-4" />
                <span>{referrerCount} total</span>
            </div>
            <AdminActionBar>
                <div className="flex-1 text-sm text-ink-500">
                    View all referrer configurations and copy sharable signup URLs
                </div>
                {getReferrersLoading && <Spinner size={6} />}
            </AdminActionBar>
            <div className="flex-1 px-6 py-4">
                {getReferrersLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Spinner size={6} />
                    </div>
                ) : data.length === 0 ? (
                    <div className="text-center py-12">
                        <LinkIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2 text-sm text-gray-500">No referrers found.</div>
                    </div>
                ) : (
                    <SimpleTable table={table} />
                )}
                {data.length > 0 && (
                    <div className="pt-3">
                        <SimplePagination table={table} />
                    </div>
                )}
            </div>
        </div>
    )
}

export default ReferrerListPage
